import { useMemo, useState, useEffect } from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Button } from '@mui/material';
import { DataGrid } from '../Common';
import { StyledCellLink } from '../Common/DataGrid/CellComponents';
import { select_commonCellProps } from '../Common/DataGrid/utils';
import Routes from '../../routes';

const backButtonSx = {
    borderRadius: '20px',
    border: '1px solid rgba(0, 176, 185, 0.50)',
    background: '#FFF',
    padding: '4px 10px',
    fontSize: '14px',
    color: '#00B0B9',
    fontWeight: 500,
    textTransform: 'none',
    minWidth: 0,
    '&:hover': { background: '#f0fafb', border: '1px solid rgba(0, 176, 185, 0.50)' },
};

const LeakInspectionAssetTable = ({ year, frequency, regulations, locationName, onBack }) => {
    const [filterOptions, setFilterOptions] = useState([]);

    useEffect(() => {
        const load = async () => {
            const res = await fetch(Routes.Dashboard.LeakInspectionFilters());
            const data = await res.json();
            setFilterOptions(data.tableFilters ?? []);
        };
        load();
    }, []);

    const staticFilters = [
        { property: 'year', value: String(year) },
        { property: 'frequency', value: frequency },
        { property: 'locationName', value: locationName },
        ...(regulations || []).map(r => ({ property: 'regulations', value: r }))
    ];

    const columns = useMemo(() => [
        {
            field: 'assetId',
            headerName: 'Asset ID',
            renderCell: ({ row }) => (
                <StyledCellLink
                    path={`/Inventory/${row.assetId}`}
                    text={row.assetId}
                    from="Leak Inspections Dashboard"
                    primaryLink={true}
                />
            )
        },
        {
            field: 'assetName',
            headerName: 'Asset Name',
            renderCell: ({ row }) => (
                <StyledCellLink
                    path={`/Inventory/${row.assetId}`}
                    text={row.assetName}
                    from="Leak Inspections Dashboard"
                    primaryLink={true}
                />
            )
        },
        {
            field: 'locationName',
            headerName: 'Location Name',
            ...select_commonCellProps
        },
        {
            field: 'regionName',
            headerName: 'Region/Division',
            ...select_commonCellProps
        },
        {
            field: 'serviceCompanyName',
            headerName: 'Service Company',
            ...select_commonCellProps
        },
        {
            field: 'required',
            headerName: 'Required',
            type: 'number'
        },
        {
            field: 'completed',
            headerName: 'Completed',
            type: 'number'
        },
        {
            field: 'missed',
            headerName: 'Missed',
            type: 'number'
        },
        {
            field: 'complianceRate',
            headerName: '% Compliance',
            type: 'number',
            renderCell: ({ row }) => row.complianceRate != null ? `${row.complianceRate}%` : '-'
        },
        {
            field: 'inspectionsMissing',
            headerName: 'Inspections Missing'
        },
        {
            field: 'applicableRegulations',
            headerName: 'Applicable Regulation(s)'
        },
    ], []);

    const backButton = onBack ? (
        <Button onClick={onBack} variant="outlined" startIcon={<ChevronLeftIcon />} sx={backButtonSx}>BACK</Button>
    ) : null;

    return (
        <DataGrid
            url={Routes.Dashboard.LeakInspectionAssets(false)}
            exportButtonURL={Routes.Dashboard.LeakInspectionAssets(true)}
            exportFileName={`LeakInspections_Assets_${year}_${frequency}`}
            exportButtonAlt="Export Assets (.csv)"
            columns={columns}
            filterOptions={filterOptions}
            staticFilters={staticFilters}
            noResultText="No assets found."
            noResultText2="Change your filters or check back later."
            toolbarLeftContent={backButton}
        />
    );
};

export default LeakInspectionAssetTable;
